/**
 * Filter-clause builders for the Orders list origin/destination filters.
 *
 * An order matches a selected location when the location appears on the parent
 * order's ship-from/ship-to columns OR on ANY leg endpoint. A middle stop of a
 * multi-leg trip (DAL -> ELP -> PHX) is both a drop and a pickup, so ELP must
 * match whether the user filters it as origin or as destination.
 */

export interface EndpointLocation {
  id: string;
  street_address?: string | null;
  city?: string | null;
}

/** Escape commas/parens in PostgREST `.or()` values by wrapping in quotes. */
export function escOrValue(v: string): string {
  return `"${(v ?? "").replace(/"/g, '\\"')}"`;
}

/**
 * Clauses matching a location on a single side of a row:
 *   1. `<prefix>_location_id` equals the location id
 *   2. `<prefix>_street_address` ilike <addr> (case-insensitive exact)
 * The street clause is skipped when blank so we never match every empty row.
 *
 * Deliberately NO city clause (unlike `resolveOrderEndpoint`'s display
 * fallback): several facilities share a city (CLIJ-DAL-01 and VAS-DAL-01 are
 * both in Hutchins, TX), and a city match would return every order in that
 * city under whichever facility was selected.
 */
export function endpointSideClauses(
  prefix: string,
  locs: EndpointLocation[],
): string[] {
  const parts: string[] = [];
  for (const loc of locs) {
    parts.push(`${prefix}_location_id.eq.${loc.id}`);
    const addr = (loc.street_address ?? "").trim();
    if (addr) parts.push(`${prefix}_street_address.ilike.${escOrValue(addr)}`);
  }
  return parts;
}

/** Clauses against `orders.ship_from_*` / `orders.ship_to_*` for one side. */
export function buildOrderEndpointClauses(
  side: "from" | "to",
  locs: EndpointLocation[],
): string[] {
  return endpointSideClauses(`ship_${side}`, locs);
}

/**
 * PostgREST `.or()` string for the `legs` table matching the location on the
 * SAME side as the filter: origin filters match leg pickups, destination
 * filters match leg drops. A middle stop still matches both filters naturally,
 * since it is the drop of leg N and the pickup of leg N+1.
 * Returns null when there is nothing to match.
 */
export function buildLegEndpointOr(
  side: "from" | "to",
  locs: EndpointLocation[],
): string | null {
  if (locs.length === 0) return null;
  const parts = endpointSideClauses(`ship_${side}`, locs);
  return parts.length > 0 ? parts.join(",") : null;
}

/** Parent-order endpoint columns embedded on a leg row (`orders!inner(...)`). */
export type ParentEndpointColumns = Partial<
  Record<
    | "ship_from_location_id"
    | "ship_from_street_address"
    | "ship_from_city"
    | "ship_to_location_id"
    | "ship_to_street_address"
    | "ship_to_city",
    string | null
  >
>;

/** Columns to embed from `orders` in the legs pre-query for one side. */
export function parentEndpointSelect(side: "from" | "to"): string {
  const p = `ship_${side}`;
  return `status,${p}_location_id,${p}_street_address,${p}_city`;
}

/**
 * JS mirror of `endpointSideClauses` for a parent row: true only when the
 * parent's own clauses are CERTAIN to match it server-side. Kept conservative
 * (plain case-insensitive equality/suffix, no ilike wildcard expansion) so a
 * false "no" just keeps a redundant id and never drops a real match.
 */
export function parentMatchesEndpoint(
  side: "from" | "to",
  parent: ParentEndpointColumns | null | undefined,
  locs: EndpointLocation[],
): boolean {
  if (!parent) return false;
  const locId = parent[`ship_${side}_location_id`];
  const addr = (parent[`ship_${side}_street_address`] ?? "").toLowerCase();
  const city = (parent[`ship_${side}_city`] ?? "").toLowerCase();
  return locs.some((loc) => {
    if (locId && locId === loc.id) return true;
    const a = (loc.street_address ?? "").trim().toLowerCase();
    if (a && addr === a) return true;
    const c = (loc.city ?? "").trim().toLowerCase();
    return !!c && city.endsWith(c);
  });
}

/**
 * Order ids that only a leg (not the parent row) can contribute. Legs whose
 * parent already matches the parent clauses are dropped: that covers leg 1's
 * pickup (mirrored onto `orders.ship_from_*`) and the last leg's drop
 * (mirrored onto `orders.ship_to_*`, see `createMutation`), which were the
 * bulk of the inlined `id.in.(...)` list (DTP-109122). Deduped, order kept.
 */
export function legOnlyOrderIds(
  side: "from" | "to",
  legRows: Array<{ order_id: string | null; orders?: ParentEndpointColumns | null }>,
  locs: EndpointLocation[],
): string[] {
  const ids = new Set<string>();
  for (const row of legRows) {
    if (!row.order_id || ids.has(row.order_id)) continue;
    if (parentMatchesEndpoint(side, row.orders, locs)) continue;
    ids.add(row.order_id);
  }
  return [...ids];
}

/**
 * PostgREST filters travel in the request URL, so an id list that grows into
 * the tens of kilobytes makes the browser/CDN reject the request outright
 * ("Failed to fetch"). Keep the whole `or=` payload under a safe budget.
 */
export const MAX_OR_CLAUSE_CHARS = 4000;

export class EndpointFilterTooBroadError extends Error {
  constructor() {
    super(
      "That location matches too many trips to filter in one request. Narrow the date range and try again.",
    );
    this.name = "EndpointFilterTooBroadError";
  }
}

/** Fold leg-matched parent ids into the order-level clauses. */
export function withLegOrderIds(
  orderClauses: string[],
  legOrderIds: string[],
): string | null {
  const parts = [...orderClauses];
  if (legOrderIds.length > 0) parts.push(`id.in.(${legOrderIds.join(",")})`);
  if (parts.length === 0) return null;
  const joined = parts.join(",");
  if (joined.length > MAX_OR_CLAUSE_CHARS) throw new EndpointFilterTooBroadError();
  return joined;
}

/**
 * Inclusive-overlap bounds for the Orders date filter: a trip matches when it
 * starts before the range ends AND finishes after the range begins.
 */
export function overlapsRange(
  trip: { start: Date | string; end: Date | string },
  range: { start: Date | string; end: Date | string },
): boolean {
  const t0 = new Date(trip.start).getTime();
  const t1 = new Date(trip.end).getTime();
  const r0 = new Date(range.start).getTime();
  const r1 = new Date(range.end).getTime();
  return t0 < r1 && t1 >= r0;
}
